import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import React from "react";
import { createRoot } from "react-dom/client";
import CVTemplate from "@/components/CVTemplate";
import ProspectorTemplate from "@/components/templates/ProspectorTemplate";
import VanguardTemplate from "@/components/templates/VanguardTemplate";
import SimpleTemplate from "@/components/templates/SimpleTemplate";
import MinimalistTemplate from "@/components/templates/MinimalistTemplate";
import { type CVData } from "@/lib/cvParser";

export async function generateCVBlob(data: CVData, template: string = "classic"): Promise<Blob> {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "-99999px";
  container.style.left = "0px";
  container.style.width = "794px";
  container.style.margin = "0";
  container.style.padding = "0"; // Templates handle their own padding
  container.style.boxSizing = "border-box";
  container.style.backgroundColor = "#ffffff";
  document.body.appendChild(container);

  // Inject Inter font specifically for the generator
  const fontLink = document.createElement("link");
  fontLink.href = "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700&display=swap";
  fontLink.rel = "stylesheet";
  document.head.appendChild(fontLink);

  const root = createRoot(container);
  const normalizedTemplate = template.toLowerCase();

  // Render the component asynchronously
  await new Promise<void>((resolve) => {
    root.render(
      <div id="pdf-gen-root" style={{ width: '794px', backgroundColor: '#ffffff' }}>
        {normalizedTemplate === "prospector" ? (
          <ProspectorTemplate data={data} />
        ) : normalizedTemplate === "vanguard" ? (
          <VanguardTemplate data={data} />
        ) : normalizedTemplate === "simple" ? (
          <SimpleTemplate data={data} />
        ) : normalizedTemplate === "minimalist" ? (
          <MinimalistTemplate data={data} />
        ) : (
          <CVTemplate data={data} />
        )}
      </div>
    );
    // Wait for fonts to be ready or fallback to timeout
    if ('fonts' in document) {
      document.fonts.ready.then(() => setTimeout(resolve, 500));
    } else {
      setTimeout(resolve, 800);
    }
  });

  try {
    const canvas = await html2canvas(container, {
      scale: 3,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      width: 794,
      windowWidth: 794,
      onclone: (clonedDoc) => {
        const rootContent = clonedDoc.getElementById('pdf-gen-root');
        if (rootContent) {
          rootContent.style.width = "794px";
          rootContent.style.height = "auto";
          rootContent.style.overflow = "visible";
        }

        // Global PDF typography fixes
        clonedDoc.querySelectorAll('*').forEach((el: any) => {
          el.style.webkitFontSmoothing = "antialiased";
          el.style.mozOsxFontSmoothing = "grayscale";

          // Force box-sizing
          el.style.boxSizing = "border-box";
        });

        // Vanguard-only: prevent job/education entries from splitting across pages.
        // NOTE: html2canvas ignores CSS page-break rules — we must use spacers.
        if (normalizedTemplate === "vanguard") {
          // This PAGE_HEIGHT must match (pageHeight/pageWidth * canvas.width) used below in the slicer.
          // At scale=3, canvas.width = 794*3 = 2382. A4 ratio = 297/210. nominalPagePx = floor(297/210 * 2382) = 3367.
          // In original px (before scale): 3367 / 3 = 1122.33 → use 1122.
          const PAGE_HEIGHT_PX = 1122;

          const templateRoot = clonedDoc.getElementById('cv-template-root') || clonedDoc.getElementById('pdf-gen-root');

          const getOffsetTopFromRoot = (el: HTMLElement, root: HTMLElement): number => {
            let top = 0;
            let curr: HTMLElement | null = el;
            while (curr && curr !== root) {
              top += curr.offsetTop;
              curr = curr.offsetParent as HTMLElement;
            }
            return top;
          };

          if (templateRoot) {
            const entries = Array.from(clonedDoc.querySelectorAll('.vanguard-work-entry, .vanguard-education-entry')) as HTMLElement[];
            entries.forEach((entry) => {
              const top = getOffsetTopFromRoot(entry, templateRoot as HTMLElement);
              const height = entry.offsetHeight;
              const bottom = top + height;

              const pageOfTop = Math.floor(top / PAGE_HEIGHT_PX);
              const pageOfBottom = Math.floor(bottom / PAGE_HEIGHT_PX);

              if (pageOfTop !== pageOfBottom) {
                // Push entry to start of next page
                const nextPageTop = (pageOfTop + 1) * PAGE_HEIGHT_PX;
                const gap = nextPageTop - top - 80; // Reduce to match page 1 top margin
                const spacer = clonedDoc.createElement('div');
                spacer.style.cssText = `display:block;height:${gap}px;width:100%;margin:0;padding:0;`;
                entry.parentNode!.insertBefore(spacer, entry);
                entry.style.marginTop = '0';
              }
            });
          }
        }

        if (normalizedTemplate === "vanguard") {
          clonedDoc.querySelectorAll('.vanguard-skill-pill').forEach((pill: any) => {
            pill.style.setProperty('display', 'inline-block', 'important');
            pill.style.setProperty('padding', '0px 12px 12px 12px', 'important'); // Final bit more UP
            pill.style.setProperty('line-height', '1.2', 'important');
            pill.style.setProperty('vertical-align', 'middle', 'important');
            pill.style.setProperty('min-height', '24px', 'important'); // min-height instead of fixed height
            pill.style.setProperty('height', 'auto', 'important');
            pill.style.setProperty('margin-right', '8px', 'important');
            pill.style.setProperty('margin-bottom', '8px', 'important');
            pill.style.setProperty('background-color', '#f1f5f9', 'important');
            pill.style.setProperty('border', '1px solid #e2e8f0', 'important');
            pill.style.setProperty('border-radius', '12px', 'important'); // Slightly less round for multiline

            pill.querySelectorAll('*').forEach((child: any) => {
              child.style.setProperty('line-height', '1.2', 'important');
              child.style.setProperty('vertical-align', 'middle', 'important');
              child.style.setProperty('display', 'inline', 'important');
              child.style.setProperty('margin', '0', 'important');
              child.style.setProperty('padding', '0', 'important');
            });
          });

          clonedDoc.querySelectorAll('.vanguard-contact-item, .vanguard-date-row, .vanguard-references-row').forEach((row: any) => {
            row.style.setProperty('display', 'flex', 'important');
            row.style.setProperty('flex-direction', 'row', 'important');
            row.style.setProperty('align-items', 'center', 'important');
            row.style.setProperty('min-height', '24px', 'important');
            row.style.setProperty('overflow', 'visible', 'important');
          });

          clonedDoc.querySelectorAll('.vanguard-contact-item svg, .vanguard-date-row svg, .vanguard-references-row svg').forEach((svg: any) => {
            svg.style.setProperty('display', 'inline-block', 'important');
            svg.style.setProperty('vertical-align', 'middle', 'important');
            svg.style.setProperty('margin', '0px 6px 0px 0px', 'important');
            svg.style.setProperty('position', 'static', 'important');
            svg.style.setProperty('overflow', 'visible', 'important');
            svg.style.setProperty('flex-shrink', '0', 'important');
            svg.style.setProperty('transform', 'translateY(2px)', 'important');
          });
        }

        if (normalizedTemplate === "prospector") {
          // Force the wrapper to table layout so sidebar doesn't reflow tall off-screen
          const wrapper = clonedDoc.querySelector('.prospector-wrapper') as HTMLElement;
          if (wrapper) {
            wrapper.style.display = 'table';
            wrapper.style.width = '794px';
            wrapper.style.tableLayout = 'fixed';
          }
          const sidebar = clonedDoc.querySelector('.prospector-sidebar') as HTMLElement;
          const content = clonedDoc.querySelector('.prospector-content') as HTMLElement;
          if (sidebar && content) {
            sidebar.style.display = 'table-cell';
            sidebar.style.width = '238px';
            sidebar.style.verticalAlign = 'top';
            content.style.display = 'table-cell';
            content.style.width = '556px';
            content.style.verticalAlign = 'top';
          }
        }

        // Classic template fixes
        // Force heavy-duty CSS overrides via a style tag (more robust than individual property assignment)
        const styleTag = clonedDoc.createElement('style');
        styleTag.innerHTML = `
          [data-pdf-id="cv-name-container"] {
            display: block !important;
            height: auto !important;
            margin-bottom: 8px !important;
            padding: 0 !important;
            clear: both !important;
          }
          [data-pdf-id="cv-name-container"] div, 
          [data-pdf-id="cv-name-container"] span {
            font-size: 45px !important;
            line-height: 1.2 !important;
            display: block !important;
          }
          [data-pdf-id="cv-title-container"] {
            display: block !important;
            margin-top: 0px !important;
            margin-bottom: 12px !important;
            padding: 0 !important;
            clear: both !important;
          }
          .section-header-box {
            display: inline-block !important;
            height: 24px !important;
            padding: 0px 12px 10px 12px !important;
            line-height: 1 !important;
            vertical-align: middle !important;
          }
          .section-header-box * {
            line-height: 1 !important;
            vertical-align: middle !important;
            display: inline !important;
            margin: 0 !important;
          }
        `;

        if (normalizedTemplate === "classic" || normalizedTemplate === "") {
          // Keep these as secondary safeguards
          const nameContainer = clonedDoc.querySelector('[data-pdf-id="cv-name-container"]') as HTMLElement;
          const titleContainer = clonedDoc.querySelector('[data-pdf-id="cv-title-container"]') as HTMLElement;

          if (nameContainer) {
            nameContainer.style.setProperty('display', 'block', 'important');
            nameContainer.style.setProperty('height', 'auto', 'important');
            nameContainer.style.setProperty('margin-bottom', '8px', 'important');
          }

          if (titleContainer) {
            titleContainer.style.setProperty('display', 'block', 'important');
            titleContainer.style.setProperty('margin-top', '0px', 'important');
            titleContainer.style.setProperty('margin-bottom', '12px', 'important');
          }

          // Fix 2: Section header boxes - Nudging text UP with 12px bottom padding
          clonedDoc.querySelectorAll('.section-header-box').forEach((clonedBox: any) => {
            clonedBox.style.setProperty('background-color', '#000000', 'important');
            clonedBox.style.setProperty('color', '#ffffff', 'important');
            clonedBox.style.setProperty('display', 'inline-block', 'important');
            clonedBox.style.setProperty('height', '24px', 'important');
            clonedBox.style.setProperty('padding', '0px 12px 10px 12px', 'important');
            clonedBox.style.setProperty('line-height', '1', 'important');
            clonedBox.style.setProperty('vertical-align', 'middle', 'important');

            clonedBox.querySelectorAll('*').forEach((child: any) => {
              child.style.setProperty('color', '#ffffff', 'important');
              child.style.setProperty('background-color', 'transparent', 'important');
              child.style.setProperty('line-height', '1', 'important');
              child.style.setProperty('display', 'inline', 'important');
              child.style.setProperty('vertical-align', 'middle', 'important');
              child.style.setProperty('margin', '0', 'important');
            });
          });

          clonedDoc.head.appendChild(styleTag);
          const underline = clonedDoc.querySelector('.header-underline') as HTMLElement;
          if (underline) {
            underline.style.backgroundColor = "#000000";
            underline.style.height = "3px";
          }
        }

        // FORCE TABLE LAYOUT FOR HEADERS (Ensures left/right alignment in PDF)
        clonedDoc.querySelectorAll('.experience-header-row').forEach((row: any) => {
          row.style.display = 'table';
          row.style.width = '100%';
          row.style.tableLayout = 'fixed';

          const children = Array.from(row.children);
          if (children.length >= 2) {
            const left = children[0] as HTMLElement;
            const right = children[1] as HTMLElement;

            left.style.display = 'table-cell';
            left.style.textAlign = 'left';
            left.style.verticalAlign = 'baseline';

            right.style.display = 'table-cell';
            right.style.textAlign = 'right';
            right.style.verticalAlign = 'baseline';
            right.style.width = '150px'; // Give enough space for dates
          }
        });

        const line = clonedDoc.querySelector('.header-underline');
        if (line) (line as HTMLElement).style.backgroundColor = "#000000";
      }
    });

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const fullHeightMm = (canvas.height / canvas.width) * pageWidth;

    if (fullHeightMm <= pageHeight) {
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, pageWidth, fullHeightMm, undefined, "SLOW");
    } else {
      const nominalPagePx = Math.floor((pageHeight / pageWidth) * canvas.width);
      let yOffset = 0;
      let firstPage = true;

      // Scan pixel data of a row to detect if it's mostly blank (safe to break on)
      const isRowBlank = (ctx: CanvasRenderingContext2D, y: number): boolean => {
        const w = canvas.width;
        // Sample every 15th column for performance
        const step = 15;
        const sampleCount = Math.floor(w / step);
        let darkPixels = 0;
        const imageData = ctx.getImageData(0, y, w, 1);
        const data = imageData.data;
        for (let x = 0; x < w; x += step) {
          const idx = x * 4;
          const r = data[idx], g = data[idx + 1], b = data[idx + 2], a = data[idx + 3];
          // Non-white, visible pixel
          if (a > 128 && (r + g + b) < 600) darkPixels++;
        }
        return darkPixels <= 2;
      };

      const ctx = canvas.getContext('2d')!;

      while (yOffset < canvas.height - 5) {
        const remaining = canvas.height - yOffset;
        let sliceH: number = remaining <= nominalPagePx ? remaining : nominalPagePx;

        // Skip near-empty trailing slices (blank page artifact).
        if (sliceH < 100) break;

        // Smart page break: if there's a next page, find the nearest blank row
        // above the cut to avoid splitting text across pages.
        if (remaining > nominalPagePx) {
          const proposedBreak = yOffset + sliceH;
          const searchUp = 80; // pixels to search upward
          const minY = yOffset + 80; // don't make a page too short
          for (let offset = 0; offset <= searchUp; offset++) {
            const checkY = proposedBreak - offset;
            if (checkY < minY) break;
            if (isRowBlank(ctx, checkY)) {
              sliceH = checkY - yOffset;
              break;
            }
          }
        }

        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = sliceH;
        const sCtx = slice.getContext("2d")!;
        sCtx.fillStyle = "#ffffff";
        sCtx.fillRect(0, 0, slice.width, slice.height);
        sCtx.drawImage(canvas, 0, -yOffset);

        const sliceHeightMm = (sliceH / canvas.width) * pageWidth;
        if (!firstPage) pdf.addPage();
        pdf.addImage(slice.toDataURL("image/png"), "PNG", 0, 0, pageWidth, sliceHeightMm, undefined, "SLOW");

        yOffset += sliceH;
        firstPage = false;
      }
    }

    return pdf.output("blob");
  } finally {
    root.unmount();
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  }
}

